import React, { useCallback, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { GestureResponderEvent } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { NFModel } from '@/domain/models'
import { OpenNfse, OpenNfseParams } from '@/domain/usecases'
import { ActivityIndicator, Box, CardBase, dateAdapterAtom, IconButton, Text } from '@/presentation/components'
import { useErrorHandler, useFormat, useNotify } from '@/presentation/hooks'
import { isLoadingOpenNfState, nfsConfigState } from '../atom'
import { styles } from './styles'

type CardNfseProps = {
  nfse: NFModel
  openNfse: OpenNfse
  index: number
}

export const CardNfse: React.FC<CardNfseProps> = ({ nfse, openNfse, index }) => {
  const notify = useNotify()
  const { formatCurrency } = useFormat()
  const nfsConfigs = useRecoilValue(nfsConfigState)
  const dateAdapter = useRecoilValue(dateAdapterAtom)
  const [currentIndex, setCurrentIndex] = useState(-1)
  const [isLoadingOpenNfse, setLoadingOpenNfse] = useRecoilState(isLoadingOpenNfState)
  const handleError = useErrorHandler()

  const openMenu = (index: number): void => setCurrentIndex(index)
  const closeMenu = (): void => setCurrentIndex(-1)

  const handleOpenNfse = useCallback((event: GestureResponderEvent, nf: NFModel) => {
    event.preventDefault()

    setLoadingOpenNfse(true)
    const openNfseParams: OpenNfseParams = {
      codEmpresa: nf.empresa,
      codUnidade: nf.unidade,
      numeroRPS: nf.notaNumero,
      serieRps: nf.notaSerie,
      linkAutenticidade: nf.linkAutenticidade,
      utilizarDevExpress: nf.utilizarDevExpress
    }

    openNfse
      .open(openNfseParams)
      .then(() => notify.show(`A Nfse ${nf.notaNumero}/${nf.notaSerie} foi aberta em uma nova aba`))
      .catch(handleError)
      .finally(() => setLoadingOpenNfse(false))
  }, [])

  return (
    <CardBase>
      <Box style={styles.title}>
        <Text style={styles.bold}>{nfse.notaNumeroNFSE}/{nfse.notaSerie}</Text>
        {!isLoadingOpenNfse &&
          <Box style={styles.buttons}>
            {((nfse.linkAutenticidade || nfse.utilizarDevExpress) && !nfsConfigs.ocultaLink) &&
              <IconButton
                testID='notas-servicos-abrir-nfse'
                onPress={(event) => handleOpenNfse(event, nfse)}
                icon={props => <MaterialCommunityIcons name="file-link-outline" {...props} />}
              />
            }
            {nfse.linkConsultaNotaPortal &&
              <IconButton
                testID='notas-servicos-consulta'
                onPress={() => window.open(nfse.linkConsultaNotaPortal, '_blank')}
                icon={props => <MaterialCommunityIcons name="file-search-outline" {...props} />}
              />
            }
          </Box>
        }
      </Box>
      {(isLoadingOpenNfse && currentIndex === index)
        ? <ActivityIndicator size='large' />
        : <>
          <Text><span style={styles.bold}>Valor: </span>{formatCurrency(nfse.notaValor)}</Text>
          <Text><span style={styles.bold}>Emissão: </span>{dateAdapter.format(dateAdapter.parse(nfse.notaData), 'DD/MM/YYYY')}</Text>
        </>
      }
    </CardBase>
  )
}
